import { useUnitLabels } from 'lib/spaceLabels'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { forwardRef, Ref, useMemo } from 'react'

import { buildSpaceDetailHref } from 'features/space/lib/buildSpaceDetailHref'
import {
  isLockerLocationUnit,
  isSpaceLocationUnit,
  LocationUnit,
} from 'state/ducks/locationUnit/models'
import subCategories from '../../../data/sub_categories.json'
import LabelComponent from '../../labels/LabelComponent'
import RatingComponent from '../../ratings/RatingComponent'
import styles from './LocationUnitCard.module.scss'

interface Props {
  unit: LocationUnit
  selected: boolean
  encryptedId: string
  ref?: Ref<HTMLLIElement>
  nameTag?: string
}

const LocationUnitName: React.FC<{ unit: LocationUnit; i18n: any }> = ({ unit, i18n }) => {
  return (
    <strong className={styles.nameText}>{i18n.language === 'ja' ? unit.name : unit.enName}</strong>
  )
}

const LocationUnitPrice: React.FC<{ unit: LocationUnit }> = ({ unit }) => {
  const { t } = useTranslation()

  if (isSpaceLocationUnit(unit)) {
    return (
      <div className={styles.price}>
        <span className={styles.pricePer24}>{t('SPACES.CARD.PRICE_PER_24')}</span>
        {unit.bagSize > 0 && unit.smallLockerNum > 0 && (
          <span className={styles.priceBag}>
            <img src="/bag_icon_02_blue.svg" alt={t('META.PLACEHOLDER_LUGGAGE_ICON')} />
            {unit.bagSize}
          </span>
        )}
        {unit.suitcaseSize > 0 && unit.largeLockerNum > 0 && (
          <span className={styles.priceCarry}>
            <img src="/suitcase_icon_02_blue.svg" alt={t('META.PLACEHOLDER_LUGGAGE_ICON')} />
            {unit.suitcaseSize}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className={styles.price}>
      <span className={styles.pricePer24}>{t('SPACES.CARD.PRICE_PER_24')}</span>
      {unit.smallLockerPrice > 0 && unit.smallLockerNum > 0 && (
        <span className={styles.priceBag}>
          <img src="/locker_small.svg" alt={t('META.PLACEHOLDER_LUGGAGE_ICON')} />
          {unit.smallLockerPrice}
        </span>
      )}
      {unit.mediumLockerPrice > 0 && unit.mediumLockerNum > 0 && (
        <span className={styles.priceBag}>
          <img src="/locker_medium.svg" alt={t('META.PLACEHOLDER_LUGGAGE_ICON')} />
          {unit.mediumLockerPrice}
        </span>
      )}
      {unit.largeLockerPrice > 0 && unit.largeLockerNum > 0 && (
        <span className={styles.priceBag}>
          <img src="/locker_large.svg" alt={t('META.PLACEHOLDER_LUGGAGE_ICON')} />
          {unit.largeLockerPrice}
        </span>
      )}
    </div>
  )
}

const LocationUnitCard: React.FC<Props> = forwardRef<HTMLLIElement, Props>(
  ({ unit, selected, encryptedId, nameTag }: Props, ref?: Ref<HTMLLIElement>): JSX.Element => {
    const { t, i18n } = useTranslation()
    const { locale } = useRouter()
    const subCategory = isSpaceLocationUnit(unit)
      ? subCategories.find((sc) => sc.id === unit.subCategoryId)
      : undefined
    let subCategoryName: string | undefined
    if (isLockerLocationUnit(unit)) {
      subCategoryName = t('COMMON.COIN_LOCKER.NAME')
    } else if (subCategory) {
      switch (i18n.language) {
        case 'ja':
          subCategoryName = subCategory.name
          break
        case 'en':
          subCategoryName = subCategory.en_name
          break
        case 'zh-TW':
          subCategoryName = subCategory.zh_tw_name
          break
        default:
          subCategoryName = subCategory.en_name || subCategory.name
      }
    }
    const labels = useUnitLabels(unit)

    let isShowLabels = false
    labels.map((label) => {
      if (label.isPermit) {
        isShowLabels = true
        return
      }
    })

    let averageRating: number = 5
    if (unit.averageRating !== null && unit.averageRating !== 0) {
      averageRating = unit.averageRating
    }

    const unavailableMessage = useMemo(() => {
      const messages = [
        t('message_on_inactive_space_preview_card_a'),
        t('message_on_inactive_space_preview_card_b'),
        t('message_on_inactive_space_preview_card_c'),
      ]

      if (!unit.isAvailable) {
        return messages[Math.floor(Math.random() * messages.length)]
      }
    }, [unit.isAvailable, t])

    const link = useMemo(() => {
      const spaceType = isSpaceLocationUnit(unit)
        ? 'space'
        : unit.type === 'pudo_locker'
        ? 'pudo_locker'
        : 'locker_cluster'

      return (
        buildSpaceDetailHref({
          locale,
          spaceType,
          spaceUuid: unit.id,
          encryptedId,
        }) ?? '#'
      )
    }, [encryptedId, locale, unit])

    return (
      <li ref={ref} className={`${styles.space} ${selected ? styles.selected : ''}`}>
        {!unit.isAvailable && (
          <Link href={link} passHref>
            <div className={styles.unavailableOverlay}>
              <p>{unavailableMessage}</p>
            </div>
          </Link>
        )}
        <Link href={link} passHref>
          <a className={styles.spaceLink}>
            <div className={styles.spaceImage}>
              <img
                src={`${
                  unit.mainImageUrl === '/space_photo_images/map_thumbnails/missing.webp' ||
                  unit.mainImageUrl === null
                    ? '/default_space_image.webp'
                    : unit.mainImageUrl
                }`}
                alt={
                  unit.mainImageUrl === '/space_photo_images/map_thumbnails/missing.webp' ||
                  unit.mainImageUrl === null
                    ? `${t('SPACES.CARD.SPACE_DEFAULT_IMAGE')}`
                    : `${t('SPACES.CARD.SPACE_IMAGE', { spaceImage: unit.name })}`
                }
              />
            </div>
            <div className={styles.detailInfo}>
              {nameTag ? (
                <>
                  {nameTag === 'h1' && (
                    <h1 className={styles.name}>
                      <LocationUnitName unit={unit} i18n={i18n} />
                    </h1>
                  )}
                  {nameTag === 'h2' && (
                    <h2 className={styles.name}>
                      <LocationUnitName unit={unit} i18n={i18n} />
                    </h2>
                  )}
                  {nameTag === 'h3' && (
                    <h3 className={styles.name}>
                      <LocationUnitName unit={unit} i18n={i18n} />
                    </h3>
                  )}
                  {nameTag === 'h4' && (
                    <h4 className={styles.name}>
                      <LocationUnitName unit={unit} i18n={i18n} />
                    </h4>
                  )}
                  {nameTag === 'h5' && (
                    <h4 className={styles.name}>
                      <LocationUnitName unit={unit} i18n={i18n} />
                    </h4>
                  )}
                </>
              ) : (
                <div className={styles.name}>
                  <LocationUnitName unit={unit} i18n={i18n} />
                </div>
              )}
              <div className={styles.category}>{subCategoryName}</div>
              {unit.averageRating !== null && (
                <div className={styles.rating}>
                  <span className={styles.ratingPoint}>{averageRating?.toFixed(2)}</span>
                  <RatingComponent averageRating={averageRating} />
                </div>
              )}
            </div>
            <div className={styles.otherInfo}>
              {isShowLabels && (
                <ul className={styles.labelList}>
                  {labels.map(
                    (label, index) => label.isPermit && <LabelComponent label={label} key={index} />
                  )}
                </ul>
              )}
              <LocationUnitPrice unit={unit} />
            </div>
          </a>
        </Link>
      </li>
    )
  }
)

LocationUnitCard.displayName = 'LocationUnitCard'

export default LocationUnitCard
